import { RecordFooter, useTitleChange } from '@msaf/core-react'
import { useState } from 'react'
import { useLocation } from 'react-router-dom'
import { HttpVerb } from '../../../../../../../../types/api-types'
import { WellStratumFieldsEdit } from '../../../../../../../../types/form-types'
import { FormDelegate, PageNavigation, WellMode } from '../../../../../../../../types/routing-types'
import ConfirmTransition from '../../../../../../../components/confirm-transition'
import { ERROR_MESSAGES } from '../../../../../../../components/errors/messages'
import { WellStratumEditForm } from '../../../../../../../components/well-forms/lithology/well-stratum-edit-form'
import NotesWrapperEdit from '../../../../../../../components/well-forms/notes/notes-wrapper-edit'
import EditRecordButtons from '../../../../../../../components/wells/edit-record-buttons'
import { useWellState } from '../../../../../../../context/well-context'
import { useGetWellData, useSaveWellData } from '../../../../../../../hooks/use-api'
import useEditForm from '../../../../../../../hooks/use-edit-form'
import { useParamsOr404 } from '../../../../../../../hooks/use-params-or-404'
import useToastMessage from '../../../../../../../hooks/use-toast-message'
import { SCROLL_TARGET } from '../../../../../../../services/constants'
import { triggerScrollToTop } from '../../../../../../../services/form-utils'

export function WellStratumEdit({ setIsDirty }: FormDelegate) {
  const {
    wellState: { isDraft, isDecommissionedWell, isExistingWell, councilId },
  } = useWellState()
  useTitleChange(`${isDraft ? 'New' : 'Edit'} Well | Stratum`)
  const { id: wellId, stratumId } = useParamsOr404('id', 'stratumId')

  const location = useLocation()
  const isAddingNewStratum = location?.pathname?.includes('new')
  const [formKey, setFormKey] = useState(Date.now())

  const urls: PageNavigation = {
    requestUrl:
      isAddingNewStratum && stratumId === WellMode.NEW
        ? `wells/${wellId}/stratum/`
        : `wells/${wellId}/stratum/${stratumId}/edit`,
    cancelRoute: isAddingNewStratum
      ? `/wells/${wellId}/edit/lithology`
      : `/wells/${wellId}/view/lithology/stratum/${stratumId}`,
    cancelRouteState: { activeTab: 1 },
    saveRouteNewId: (id) => `/wells/${wellId}/edit/lithology/stratum/${id}`,
    saveAndReturnRoute: `/wells/${wellId}/view/lithology/stratum/${stratumId}`,
    saveAndContinueRoute: (id) => {
      if (isAddingNewStratum) {
        // Pushing to same URL doesn't clear form state - do it manually
        resetUseEditFormState()
        // Update form key to force re-mount of form components to properly clear text/number inputs
        setFormKey(Date.now())
        setTimeout(() => triggerScrollToTop(SCROLL_TARGET), 200)
        return `/wells/${wellId}/edit/lithology/stratum/new`
      }
      return `/wells/${wellId}/view/lithology/stratum/${id}`
    },
    navigateToSaveAndContinueRouteState: { activeTab: 1, isAddingNewStratum: true },
    method: isAddingNewStratum && stratumId === WellMode.NEW ? HttpVerb.POST : HttpVerb.PUT,
    saveAndNavigateLabel: isAddingNewStratum ? 'Save and add another' : 'Save and return',
  }

  const stateQueryResult = useGetWellData(urls.requestUrl, stratumId !== WellMode.NEW)
  const saveMutationResult = useSaveWellData(urls.requestUrl, urls.method)

  const {
    fields,
    error,
    isLoading,
    isSaveError,
    isSaved,
    setValue,
    handleSave,
    addField,
    removeField,
    resetUseEditFormState,
    editField,
    validationErrors,
  } = useEditForm<WellStratumFieldsEdit>({
    stateQueryResult,
    saveMutationResult,
    setIsDirty,
  })

  useToastMessage({
    errorMessage: validationErrors ? ERROR_MESSAGES.VALIDATION_ERROR : ERROR_MESSAGES.SAVE_FAILED,
    successMessage: 'Save successful',
    error,
    isSuccess: isSaved,
    isError: isSaveError,
  })

  return (
    <>
      <WellStratumEditForm
        key={formKey}
        isLoading={isLoading}
        fields={fields}
        setValue={setValue}
        validationErrors={validationErrors ?? []}
        isNewRecord={isAddingNewStratum}
        isExistingOrDecommissioned={!isDecommissionedWell || !isExistingWell}
        councilId={councilId}
      />
      <RecordFooter
        notes={
          <NotesWrapperEdit
            notes={fields.notes ?? []}
            isLoading={isLoading}
            addField={(note) => addField('notes', note, true)}
            removeField={(index: number) => removeField('notes', index)}
            editField={(note, idx) => editField('notes', note, idx)}
            setIsDirty={setIsDirty}
          />
        }
        areActionsFixed={stratumId !== 'new'}
      >
        <EditRecordButtons
          handleSave={handleSave}
          urls={urls}
          disableButtons={fields.stratumBottom == null ? ['Save and add another'] : []}
        />
      </RecordFooter>
    </>
  )
}

export default function EditStratumWrapped() {
  const [isDirty, setIsDirty] = useState<boolean>(false)
  return (
    <ConfirmTransition isFormDirty={isDirty}>
      <WellStratumEdit setIsDirty={setIsDirty} />
    </ConfirmTransition>
  )
}
